import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

@Component({
	selector: 'app-truck-summary-report-performance',
	templateUrl: './truck-summary-report-performance.component.html',
	styleUrls: ['./truck-summary-report-performance.component.css']
})
export class TruckSummaryReportPerformanceComponent implements OnChanges{
	@Input() data!: any;
	@Input() period!: string;
	@Input() selectedDays!: string;
	@Input() totalTrucks!: number;
	@Input() showTooltip: boolean = false;
	@Input() selectedBatteryVintage: string[] = ['all'];
	@Input() selectedTruckType = "All";
	@Input() truckManufacturer = "All";
	@Input() devices: any = {};
	@Input() tooltipType: string = 'device';

	constructor(
		private translateService:TranslateService,
		private siteDashboardService: SiteDashboardService,
	) {}

	scoreConfigs: any[] = [];
	showData: boolean = true;
	color!: string;
	scoreValue: number = 50;
	radius: number = 70;
	circumference: number = 2 * Math.PI * this.radius;
	dashOffset: number = 0;

	updateScoreConfigs() {
		this.scoreConfigs = [
			{
				type: 'fleet_utilization_score',
				titleKey: "battery_performance.utilization_score",
				value: 0,
				status: '',
				color: '',
				dashOffset: 0,
				maxValue: 100,
				tooltip: this.getTooltip(this.tooltipType, 'utilization')
			},
			{
				type: 'fleet_energy_usage_score',
				titleKey: "battery_performance.energy_utilization_score",
				value: 0,
				status: '',
				color: '',
				dashOffset: 0,
				maxValue: 100,
				tooltip: this.getTooltip(this.tooltipType, 'energy')
			}
		];
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!this.data)
			return this.showData = false;

		if (this.data && Object.keys(this.data).length) {
			this.dashOffset =this.circumference - (this.scoreValue / 100) * this.circumference;
			this.formatSelectedData();
		}
		
		if (changes['period'] || changes['selectedDays'] || changes['truckManufacturer'] || changes['selectedTruckType'] || changes['selectedBatteryVintage']){
			this.formatSelectedData();
		}
	}

	ngOnInit() {
		this.updateScoreConfigs();
		this.dashOffset =this.circumference - (this.scoreValue / 100) * this.circumference;
		this.formatSelectedData();
	}

	formatSelectedData() {
		let selectedData;
		if(this.period)
			selectedData = this.data?.[this.period]?.[this.selectedDays];
		else
			selectedData = this.data?.[this.selectedDays];
		if (!selectedData || !selectedData.devicesUsageScore?.length) {
			this.showData = false;
			return;
		}

		this.showData = true;

		for (const score of this.scoreConfigs) {
			score.value = this.calculateScore(score.type, selectedData);
			this.applyScoreStyle(score);
		}
	}

	applyScoreStyle(score: any) {
		const value = score.value;
		switch(true) {
			case value >= 80:
				score.color = '#2e7d32';
				score.status = this.translateService.instant('g.excellent');
			break;
			case value >= 60 && value < 80:
				score.color = '#4caf50';
				score.status = this.translateService.instant('g.good');
			break;
			case value >= 40 && value < 60:
				score.color = '#e8e80cff';
				score.status = this.translateService.instant('g.fair');
			break;
			case value >= 20 && value < 40:
				score.color = '#f9a825';
				score.status = this.translateService.instant('g.fair');
			break;
			default:
				score.color = '#d32f2f';
				score.status = this.translateService.instant('g.weak');
			break;
		}
		score.dashOffset = this.circumference - (Math.min(value, 100) / 100) * this.circumference;
	}

	calculateScore(chartType: string, selectedData: any): number {
		const scoreFields: any = { fleet_utilization_score: 'score', fleet_energy_usage_score: 'energy_score' };
		const scoreField = scoreFields[chartType];
		if (!scoreField)
			return 0;

		let totalScore = 0;
		let devicesCount = 0;

		// devicesUsageScore already carries each truck's own score, pro-rated to its own
		// installed days server-side - averaging these (instead of pooling usage over
		// this.totalTrucks) keeps a truck installed partway through the period from being
		// diluted against the full period, and a not-yet-installed truck out of the average
		// entirely (devicesUsageScore only contains devices installed at some point in range).
		for (const device of selectedData?.devicesUsageScore || []) {
			if (!this.isIncludedDevice(device.macAddress))
				continue;

			totalScore += device[scoreField];
			devicesCount++;
		}

		if (!devicesCount) {
			this.showData = false;
			return 0;
		}

		return Math.round(totalScore / devicesCount);
	}

	isIncludedDevice(macAddress: string) {
		if (this.selectedTruckType !== 'All' && this.devices?.[macAddress]?.truck_type !== this.selectedTruckType)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(this.devices?.[macAddress], this.selectedBatteryVintage))
			return false;

		if (this.truckManufacturer !== 'All' && this.devices?.[macAddress]?.truck_manufacturer !== this.truckManufacturer)
			return false;

		return true;
	}

	getTooltip(type: string, metric: string): string {
		const map: any = {
			device: {
				utilization: 'truck_utilization_scores',
				energy: 'truck_energy_usage_score'
			},
			site: {
				utilization: 'site_truck_fleet_utilization_score',
				energy: 'site_truck_fleet_energy_usage_score'
			},
			study: {
				utilization: 'study_truck_fleet_utilization_score',
				energy: 'study_truck_fleet_energy_usage_score'
			}
		};

		const safeType = map[type] ? type : 'device';

		return map[safeType][metric];
	}
}
